import React, { useEffect, useRef, useState } from "react";
import { Link } from 'react-router-dom';

import "./style.scss";
import { Header } from "./component/Header";
import { Footer } from "./component/Footer";

interface AppItem {
  id: string;
  name: string;
  link: string;
  subtitle: string;
  icon: string;
  color: string;
}

const APP_ITEMS: AppItem[] = [
  { id: "chat", name: "Khothi Chat", link: "/chats", subtitle: "Chat nhóm", icon: "fa-solid fa-comment-dots", color: "#3b82f6" },
  { id: "customer", name: "Khách hàng", link: "/customer", subtitle: "Khách hàng", icon: "fa-solid fa-user", color: "#efa54d" },
  { id: "appointments", name: "Lịch hẹn", link: "/appointments", subtitle: "Lịch hẹn", icon: "fa-solid fa-calendar", color: "#ef4d8b" },
  { id: "consultations", name: "Lịch tư vấn", link: "/consultations", subtitle: "Lịch tư vấn", icon: "fa-solid fa-calendar-check", color: "#3431e0ce" },
  { id: "execution-schedule", name: "Lịch thực hiện", link: "/execution-schedule", subtitle: "Lịch thực hiện", icon: "fa-solid fa-calendar-days", color: "#424905ce" },
  { id: "care-list", name: "Lịch CSKH", link: "/care-list", subtitle: "Lịch CSKH", icon: "fa-solid fa-headset", color: "#146304ce" },
  { id: "marketing-campaign-report", name: "BCCD Marketing", link: "/marketing-campaign-report", subtitle: "BCCD Marketing", icon: "fa-solid fa-chart-line", color: "#b81c29ce" },
  { id: "marketing-report", name: "Báo cáo", link: "/marketing-report", subtitle: "Báo cáo", icon: "fa-solid fa-chart-column", color: "#1dcf17" },
  { id: "internal-comms", name: "Truyền thông nội bộ", link: "/#", subtitle: "Trang chủ", icon: "fa-solid fa-bars-staggered", color: "#6366f1" },
  { id: "documents", name: "Quản lý tài liệu", link: "/#", subtitle: "Tài liệu", icon: "fa-solid fa-folder", color: "#f59e0b" },
  { id: "recruitment", name: "Tuyển dụng", link: "/#", subtitle: "Quản lý tuyển dụng", icon: "fa-solid fa-user-tie", color: "#0ea5e9" },
  { id: "tasks", name: "Quản lý công việc", link: "/#", subtitle: "Phê duyệt và Đề xuất", icon: "fa-solid fa-clipboard-check", color: "#10b981" },
  { id: "call-center", name: "Call center", link: "/#", subtitle: "Tổng đài CSKH", icon: "fa-solid fa-building", color: "#22c55e" },
  { id: "sales", name: "Quản lý bán hàng", link: "/#", subtitle: "Bán hàng & Đơn hàng", icon: "fa-solid fa-cart-shopping", color: "#ef4444" },
  { id: "meeting", name: "Khothi Meeting", link: "/#", subtitle: "Cuộc họp", icon: "fa-solid fa-calendar-days", color: "#ef4444" },
  { id: "warehouse", name: "Quản lý kho", link: "/#", subtitle: "Kho hàng & Vật tư", icon: "fa-solid fa-boxes-stacked", color: "#3b82f6" },
  { id: "finance", name: "Tài chính kế toán", link: "/#", subtitle: "Tài chính & Thu chi", icon: "fa-solid fa-file-invoice-dollar", color: "#f59e0b" },
];

const CHAT_ITEM_BADGE_ID = "chat";

const SIDEBAR_LINKS = [
  "TRUYỀN THÔNG NỘI BỘ",
  "TUYỂN DỤNG",
  "INFO+",
  "TÀI CHÍNH KẾ TOÁN",
  "TRÌNH KÝ",
];

function useNetworkCanvas(canvasRef: React.RefObject<HTMLCanvasElement>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let animationId = 0;

    const PARTICLE_COUNT = 100;
    const CONNECTION_DISTANCE = 150;

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;

      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > width) this.vx = -this.vx;
        if (this.y < 0 || this.y > height) this.vy = -this.vy;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(45, 212, 191, 0.5)";
        ctx.fill();
      }
    }

    let particles: Particle[] = [];

    const initParticles = () => {
      particles = Array.from({ length: PARTICLE_COUNT }, () => new Particle());
    };

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < CONNECTION_DISTANCE) {
            const opacity = 1 - distance / CONNECTION_DISTANCE;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(45, 212, 191, ${opacity * 0.2})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();
    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, [canvasRef]);
}

export default function KhothiDashboard(): JSX.Element {

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useNetworkCanvas(canvasRef);

  const filteredApps = APP_ITEMS.filter((app) =>
    app.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <div className="relative min-h-screen w-screen flex flex-col overflow-hidden bg-[#0f172a] text-gray-100 font-sans text-sm antialiased">
      {/* Tech Network Animated Background */}
      <canvas ref={canvasRef} className="network-canvas absolute inset-0 w-full h-full z-0 pointer-events-none" />
      <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(circle_at_30%_30%,rgba(30,64,175,0.4)_0%,rgba(15,23,42,0.9)_70%)]" />

      {/* Top Navigation Bar */}
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

      {/* Main Content Area */}
      <main className="flex-1 flex overflow-hidden relative w-full">
        {/* Sidebar - Hidden on mobile, visible on md+ */}
        <aside className={`
          fixed md:static
          top-16 left-0
          w-56 sm:w-48 md:w-56
          h-[calc(100vh-4rem)] md:h-full
          flex-shrink-0 flex flex-col pt-6 pl-4
          z-40 md:z-10
          bg-[#0f172a]/95 md:bg-transparent
          backdrop-blur-md md:backdrop-blur-none
          transform transition-transform duration-300 md:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          border-r border-gray-700/50 md:border-r-0
        `}>
          <div className="text-[10px] font-bold text-gray-500 mb-4 tracking-wider flex items-center">
            <div className="w-1 h-3 bg-gray-500 mr-2" />
            TẤT CẢ ỨNG DỤNG
          </div>
          <nav className="flex flex-col space-y-1">
            {SIDEBAR_LINKS.map((link) => (
              <a
                href="#top"
                key={link}
                className="text-gray-400 hover:text-white py-1.5 text-xs font-medium transition"
              >
                {link}
              </a>
            ))}
          </nav>
          <div className="mt-8 text-xs text-gray-500 hover:text-gray-300 cursor-pointer transition">
            CỘNG ĐỒNG HỎI ĐÁP, CHIA SẺ
          </div>
        </aside>

        {/* Sidebar overlay on mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 md:hidden z-30 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Center Content (Search + App Grid) */}
        <section className="flex-1 flex flex-col items-center pt-2 md:pt-6 relative z-10 w-full overflow-y-auto">
          {/* Search Bar */}
          <div className="w-full max-w-2xl mb-6 md:mb-12 px-4">
            <div className="relative bg-gray-800 bg-opacity-40 rounded-md border border-gray-700 border-opacity-50 flex items-center px-3 md:px-4 py-2 hover:bg-opacity-60 transition cursor-text">
              <i className="fa-solid fa-magnifying-glass text-gray-400 text-sm mr-3" />
              <input
                type="text"
                placeholder="Tìm kiếm ứng dụng"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border-none outline-none text-gray-200 w-full placeholder-gray-500 text-sm"
              />
            </div>
          </div>

          {/* App Grid - Responsive columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 md:gap-8 px-3 md:px-4 max-w-5xl mx-auto w-full">
            {filteredApps.map((app) => (
              <Link to={app.link} key={app.id}>
                <div className="app-icon-container flex flex-col items-center cursor-pointer group">
                  <div
                    className="w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 rounded-2xl flex items-center justify-center mb-2 md:mb-3 shadow-lg transition-all relative hover:shadow-xl hover:scale-110"
                    style={{ backgroundColor: app.color }}
                  >
                    <i className={`${app.icon} text-white text-lg sm:text-xl md:text-2xl`} />
                    {app.id === CHAT_ITEM_BADGE_ID && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                        1
                      </span>
                    )}
                  </div>
                  <span className="text-gray-200 font-medium text-xs sm:text-sm text-center line-clamp-2">{app.name}</span>
                  <span className="text-gray-500 text-[9px] sm:text-[10px] text-center line-clamp-1">{app.subtitle}</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination Dots */}
          <div className="mt-6 md:mt-8 flex space-x-2">
            <div className="w-1.5 h-1.5 rounded-full bg-white opacity-80" />
            <div className="w-1.5 h-1.5 rounded-full bg-white opacity-30" />
          </div>
        </section>
      </main>

      {/* Bottom Widgets Area */}
      <Footer />
    </div>
  );
}